/*
 * Copyright (c) 2025. Haodong JU
 */

import { hillo } from 'hillo'
import { cloudV2Url } from '@/model/dataLayer/api/SaaS/SaaSUtils'
import IKDataEntity from '@/plugins/EntityFrameWork/IKDataEntity'
import { colorList } from '@/model/dataLayer/api/dish/Category'
import dayjs from 'dayjs'
import GlobalConfig from '@/model/global/GlobalSettings'
import { saveFile } from '@/model/utils/utils'

const path = 'api/assets'
export const assetType = [{
  name: 'VOUCHER', value: 'VOUCHER'
}, {
  name: 'POINT_EXCHANGE', value: 'POINT_EXCHANGE'
}, {
  name: 'PHYSICAL_REWARD', value: 'PHYSICAL_REWARD'
}]

const entity = {
  description: {
    header: false,
    formConfig: {
      required: false
    }
  },
  image: {
    header: false,
    type: IKDataEntity.Types.Image,
    tableConfig: {
      required: false
    }
  },
  imageDisplay: {
    displayName: 'image',
    type: IKDataEntity.Types.Integer,
    form: false,
    tableConfig: {
      overwrite: true
    }
  },
  name: {
  },
  color: {
    type: IKDataEntity.Types.Color,
    formConfig: {
      default: colorList[Math.round(Math.random() * 18)],
      required: false,
      hint: 'selected_color_will_be_used_in_orderDevice'
    },
    header: false,
    tableConfig: { filterable: false }
  },
  initialStock: {
    header: false,
    formConfig: {
      default: 0,
      required: false
    }
  },
  currentStock: {
    displayName: '实际库存',
    formConfig: {
      default: 0,
      required: false
    }
  },
  distributed: {
    header: false,
    formConfig: {
      default: 0,
      required: false
    }
  },
  used: {
    header: false,
    formConfig: {
      default: 0,
      required: false
    }
  },
  expired: {
    header: false,
    formConfig: {
      default: 0,
      required: false
    }
  },
  activeSince: {
    header: false,
    type: IKDataEntity.Types.Date,
    formConfig: {
      default: dayjs().format('YYYY-MM-DD'),
      required: false
    }
  },
  activeSinceDisplay: {
    displayName: '生效时间',
    form: false
  },
  // activeSinceDate: {
  //   type: IKDataEntity.Types.Date,
  //   header: false
  // },
  // activeSinceTime: {
  //   type: IKDataEntity.Types.Time,
  //   header: false
  // },
  expiredAt: {
    header: false,
    type: IKDataEntity.Types.Date,
    formConfig: {
      required: false
    }
  },
  expiredAtDisplay: {
    displayName: '截止时间',
    form: false
  },
  claimDaysLimit: {
    header: false,
    formConfig: {
      required: false
    }
  },
  active: {
    displayName: '允许继续兑换资产',
    type: IKDataEntity.Types.Boolean,
    formConfig: {
      required: false,
      default: true
    }
  },
  assetType: {
    displayName: '资产类型',
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        selectItems: assetType,
        itemText: 'name',
        itemValue: 'value'
      }
    }
  },
  supportsRedemptionCode: {
    displayName: '允许使用兑换码兑换',
    type: IKDataEntity.Types.Boolean,
    formConfig: {
      required: false,
      default: true
    }
  },
  redemptionCodePrefix: {
    header: false,
    formConfig: {
      required: false
    }
  },
  assetConfig: {
    header: false,
    form: false,
    formConfig: {
      required: false
    }
  },
  unlimitedSupply: {
    header: false,
    displayName: '无限供应',
    type: IKDataEntity.Types.Boolean,
    formConfig: {
      required: false
    }
  }
}

const config = {
  load: async function (filter) {
    const res = (await hillo.get(cloudV2Url + path + '/by-blid/' + GlobalConfig.blId)).content.map(it => {
      it.name = it.displayInfo.name
      it.description = it.displayInfo.description
      it.image = it.displayInfo.imageUrl
      it.color = it.displayInfo.color
      it.activeSince = it.activeSince ? dayjs(it.activeSince).format('YYYY-MM-DD') : null
      it.expiredAt = it.expiredAt ? dayjs(it.expiredAt).format('YYYY-MM-DD') : null
      it.activeSinceDisplay = it.activeSince ? dayjs(it.activeSince).format('YYYY-MM-DD') : '-'
      it.expiredAtDisplay = it.expiredAt ? dayjs(it.expiredAt).format('YYYY-MM-DD') : '-'
      return it
    })
    return res
  },
  add: async function (item) {
    item.activeSince = item.activeSince ? item.activeSince + 'T00:00:00' : null
    item.expiredAt = item.expiredAt ? item.expiredAt + 'T00:00:00' : null
    item.displayInfo.imageUrl = item.displayInfo.imageUrl ? await saveFile(item.displayInfo.imageUrl) : ''
    item.blId = GlobalConfig.blId
    return (await hillo.jsonPost(cloudV2Url + path, {
      ...item
    }))
  },
  edit: async function (item) {
    item.activeSince = item.activeSince ? dayjs(item.activeSince).format('YYYY-MM-DD') + 'T00:00:00' : null
    item.expiredAt = item.expiredAt ? dayjs(item.expiredAt).format('YYYY-MM-DD') + 'T00:00:00' : null
    item.displayInfo.imageUrl = typeof item.displayInfo.imageUrl !== 'string' ? await saveFile(item.displayInfo.imageUrl) : item.displayInfo.imageUrl
    return (await hillo.jsonPost(cloudV2Url + path + '/' + item.id, {
      ...item
    })).content
  },
  remove: async function (id) {
    return (await hillo.jsonPost(cloudV2Url + path + '/' + id + '/delete', {
      id
    }))
  }
}

export default IKDataEntity.ModelFactory(entity, config)

export async function distributeAssetToUser (assetId, info) {
  // Ensure assetId is a string to prevent "Invalid UUID string" errors
  const assetIdStr = String(assetId)
  return (await hillo.jsonPost(cloudV2Url + path + '/' + assetIdStr + '/distribute', info))
}

export async function adjustInventory (assetId, info) {
  // Ensure assetId is a string to prevent "Invalid UUID string" errors
  const assetIdStr = String(assetId)
  return (await hillo.jsonPost(cloudV2Url + path + '/' + assetIdStr + '/adjust-inventory', info))
}

export async function distributeAssetToUsersList (assetId, info) {
  // Ensure assetId is a string to prevent "Invalid UUID string" errors
  const assetIdStr = String(assetId)
  return (await hillo.jsonPost(cloudV2Url + path + '/' + assetIdStr + '/distribute-bulk', info))
}

export async function getAssetDetail (assetId) {
  // Ensure assetId is a string to prevent "Invalid UUID string" errors
  const assetIdStr = String(assetId)
  const response = await hillo.get(cloudV2Url + path + '/' + assetIdStr + '/detail')

  // Process dates for display
  if (response.asset) {
    response.asset.activeSinceDisplay = response.asset.activeSince ? dayjs(response.asset.activeSince).format('YYYY-MM-DD') : '-'
    response.asset.expiredAtDisplay = response.asset.expiredAt ? dayjs(response.asset.expiredAt).format('YYYY-MM-DD') : '-'

    // Add display properties from displayInfo
    response.asset.name = response.asset.displayInfo.name
    response.asset.description = response.asset.displayInfo.description
    response.asset.image = response.asset.displayInfo.imageUrl
    response.asset.color = response.asset.displayInfo.color
  }

  // Process dates for redemption codes
  if (response.redemptionCodes) {
    response.redemptionCodes.forEach(code => {
      code.createAtDisplay = code.createdAt ? dayjs(code.createdAt).format('YYYY-MM-DD HH:mm') : '-'
      code.expiresAtDisplay = code.expiresAt ? dayjs(code.expiresAt).format('YYYY-MM-DD HH:mm') : '-'
      code.redeemedAtDisplay = code.redeemedAt ? dayjs(code.redeemedAt).format('YYYY-MM-DD HH:mm') : '-'
    })
  }

  // Process dates for distribution records
  if (response.records) {
    response.records.forEach(record => {
      record.distributedAtDisplay = record.distributedAt ? dayjs(record.distributedAt).fromNow() : '-'
      record.expiresAtDisplay = record.expiresAt ? dayjs(record.expiresAt).format('YYYY-MM-DD HH:mm') : '-'
      record.expiredAtDisplay = record.expiredAt ? dayjs(record.expiredAt).format('YYYY-MM-DD HH:mm') : '-'
      record.usedAtDisplay = record.usedAt ? dayjs(record.usedAt).fromNow() : '-'
      // Also store the original format for tooltip or detailed view
      record.distributedAtOriginal = record.distributedAt ? dayjs(record.distributedAt).format('YYYY-MM-DD HH:mm') : '-'
      record.usedAtOriginal = record.usedAt ? dayjs(record.usedAt).format('YYYY-MM-DD HH:mm') : '-'
    })
  }

  return response
}
